import { useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";

import { ADMIN_MEMBER_ZONE_MATRIX } from "../../gql/queries/eventInsights";
import type { ZoneLabel } from "../../types/graphql";
import Explainer from "./Explainer";
import { fmtInt, rgba, zoneHex, zoneIsDark, zoneRgb } from "./helpers";
import type { MemberZoneMatrix, MemberZones } from "./types";
import WorkedExample from "./WorkedExample";
import ZoneClockReference from "./ZoneClockReference";

const ZoneDot: React.FC<{ zone: ZoneLabel }> = ({ zone }) => (
  <Box
    component="span"
    sx={{
      width: 10,
      height: 10,
      borderRadius: "50%",
      backgroundColor: zoneHex(zone),
      display: "inline-block",
    }}
  />
);

/** Table 1 — current members by colour zone × tenure, adjusted (fair) vs raw (biased). */
const MemberZonePanel: React.FC = () => {
  const [method, setMethod] = useState<"adjusted" | "raw">("adjusted");
  const { data, loading, error } = useQuery(ADMIN_MEMBER_ZONE_MATRIX, {
    fetchPolicy: "cache-and-network",
  });
  const both: MemberZones | undefined = data?.adminMemberZoneMatrix;

  if (loading && !both) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">Couldn’t load members by zone. {error.message}</Alert>;
  }
  if (!both?.adjusted.grandTotal) {
    return <Alert severity="info">No member data to show.</Alert>;
  }

  const matrix: MemberZoneMatrix = method === "adjusted" ? both.adjusted : both.raw;
  const zones = matrix.zones;
  const totalByZone = new Map<ZoneLabel, number>(matrix.zoneTotals.map((c) => [c.zone, c.count]));
  let max = 0;
  matrix.rows.forEach((r) => {
    r.counts.forEach((c) => {
      max = Math.max(max, c.count);
    });
  });

  const topZone = (
    counts: { zone: ZoneLabel; count: number }[],
  ): { zone: ZoneLabel; count: number } | null => {
    let best: { zone: ZoneLabel; count: number } | null = null;
    counts.forEach((c) => {
      if (!best || c.count > best.count) {
        best = c.count > 0 ? c : best;
      }
    });
    return best;
  };

  const gridTemplate = `190px repeat(${zones.length}, minmax(52px, 1fr)) 70px 150px`;
  const headSx = {
    fontSize: 11,
    fontWeight: 600,
    color: "text.secondary",
    px: 1,
    py: 0.75,
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    justifyContent: "center",
    borderBottom: "1px solid",
    borderColor: "divider",
  } as const;

  const cells: React.ReactNode[] = [];
  cells.push(
    <Box key="corner" sx={{ ...headSx, justifyContent: "flex-start", fontWeight: 700 }}>
      Tenure ╲ Zone
    </Box>,
  );
  zones.forEach((z) => {
    cells.push(
      <Box key={`h-${z}`} sx={headSx}>
        <ZoneDot zone={z} /> {z}
      </Box>,
    );
  });
  cells.push(
    <Box key="h-total" sx={{ ...headSx, justifyContent: "flex-end" }}>
      Total
    </Box>,
    <Box key="h-top" sx={{ ...headSx, justifyContent: "flex-end" }}>
      Top zone
    </Box>,
  );

  const topCell = (counts: { zone: ZoneLabel; count: number }[], key: string): React.ReactNode => {
    const t = topZone(counts);
    return (
      <Box
        key={key}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 0.5,
          fontSize: 12,
          px: 1,
          py: 0.75,
        }}
      >
        {t ? (
          <>
            <ZoneDot zone={t.zone} /> <strong>{t.zone}</strong>
            <Box component="span" sx={{ color: "text.secondary" }}>
              {fmtInt(t.count)}
            </Box>
          </>
        ) : (
          <Box component="span" sx={{ color: "text.disabled" }}>
            —
          </Box>
        )}
      </Box>
    );
  };

  matrix.rows.forEach((r) => {
    cells.push(
      <Box
        key={`l-${r.tenure}`}
        sx={{
          fontSize: 12,
          px: 1,
          py: 0.75,
          borderBottom: "1px solid",
          borderColor: "action.hover",
        }}
      >
        {r.tenureLabel}
      </Box>,
    );
    r.counts.forEach((c) => {
      const alpha = max ? 0.1 + 0.85 * (c.count / max) : 0;
      const dark = zoneIsDark(c.zone) && alpha > 0.55;
      cells.push(
        <Tooltip
          key={`c-${r.tenure}-${c.zone}`}
          title={`${c.zone} · ${r.tenureLabel}: ${fmtInt(c.count)} members`}
          arrow
        >
          <Box
            sx={{
              fontSize: 12,
              textAlign: "center",
              py: 0.75,
              color: c.count ? (dark ? "#fff" : "text.primary") : "text.disabled",
              backgroundColor: c.count ? rgba(zoneRgb(c.zone), alpha) : "transparent",
              borderBottom: "1px solid",
              borderColor: "action.hover",
            }}
          >
            {c.count ? fmtInt(c.count) : "·"}
          </Box>
        </Tooltip>,
      );
    });
    cells.push(
      <Box
        key={`tot-${r.tenure}`}
        sx={{
          fontSize: 12,
          fontWeight: 700,
          textAlign: "right",
          px: 1,
          py: 0.75,
          borderBottom: "1px solid",
          borderColor: "action.hover",
        }}
      >
        {fmtInt(r.total)}
      </Box>,
      topCell(r.counts, `top-${r.tenure}`),
    );
  });

  // Totals row
  cells.push(
    <Box key="l-all" sx={{ fontSize: 12, fontWeight: 700, px: 1, py: 0.75 }}>
      ▦ All tenures
    </Box>,
  );
  zones.forEach((z) => {
    const v = totalByZone.get(z) ?? 0;
    cells.push(
      <Box key={`all-${z}`} sx={{ fontSize: 12, fontWeight: 600, textAlign: "center", py: 0.75 }}>
        {v ? fmtInt(v) : "·"}
      </Box>,
    );
  });
  cells.push(
    <Box
      key="all-total"
      sx={{ fontSize: 12, fontWeight: 700, textAlign: "right", px: 1, py: 0.75 }}
    >
      {fmtInt(matrix.grandTotal)}
    </Box>,
    topCell(matrix.zoneTotals, "all-top"),
  );

  // Raw vs adjusted comparison (zone totals + shift).
  const rawByZone = new Map<ZoneLabel, number>(both.raw.zoneTotals.map((c) => [c.zone, c.count]));
  const adjByZone = new Map<ZoneLabel, number>(
    both.adjusted.zoneTotals.map((c) => [c.zone, c.count]),
  );

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" mb={1.5}>
        Which <b>3-hour UTC slot</b> each member is most active in, across the eight colour zones
        and by how long they’ve been with us —{" "}
        <b>adjusted for how many events we run in each zone</b>, so it reflects real availability
        rather than our schedule. A slot is a fixed UTC window; what local time that is depends on
        where she lives (see the zone-to-local reference below).
      </Typography>
      <Explainer title="How a member’s zone is worked out (and why it’s fair)">
        A member’s zone is the UTC slot she’s usually around in — the events she attended{" "}
        <b>or hosted</b>. The obvious way — “which slot has she been in most?” — is misleading,
        because she can only turn up to events we actually run: if we schedule most events in one
        slot, everyone piles into it. So instead of counting, we compare it to the <b>chances</b>{" "}
        she had. For each zone we look at how many events were offered there versus how many she
        turned up to, and her zone is the one where she shows up most{" "}
        <b>relative to how often it’s offered</b>. To avoid being fooled by luck (the one 6am event
        we ever ran), we only trust this once she’s attended enough, and lean on her timezone
        otherwise.
      </Explainer>
      <ZoneClockReference />

      <ToggleButtonGroup
        size="small"
        exclusive
        value={method}
        onChange={(_, v) => v && setMethod(v)}
        sx={{ mb: 1.5 }}
      >
        <ToggleButton value="adjusted">Adjusted (fair)</ToggleButton>
        <ToggleButton value="raw">Raw attendance (biased)</ToggleButton>
      </ToggleButtonGroup>

      <Box sx={{ overflowX: "auto" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: gridTemplate,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            minWidth: 720,
          }}
        >
          {cells}
        </Box>
      </Box>
      <Typography variant="caption" color="textSecondary" component="p" mt={1}>
        {method === "adjusted"
          ? "Fair view: each member placed where she turns up most per chance offered."
          : "Biased view: each member placed simply where she’s attended most — this leans toward whichever slots we schedule most."}{" "}
        Each cell is shaded in its zone’s colour, deeper = more members.
      </Typography>

      {/* Comparison: how the bias shifts members */}
      <Box mt={3}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
          Raw vs. adjusted — how the bias shifts members
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          The same members, counted both ways. <b>Raw</b> piles them into the slots we schedule
          most; <b>Adjusted</b> moves them to where they actually turn up per chance. The shift is
          the bias.
        </Typography>
        <Box sx={{ overflowX: "auto" }}>
          <Table
            size="small"
            sx={{ width: "auto", "& td, & th": { border: 0, py: 0.4, pr: 3, pl: 0 } }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: 11, color: "text.secondary", fontWeight: 600 }}>
                  Zone
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontSize: 11, color: "text.secondary", fontWeight: 600 }}
                >
                  Raw (biased)
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontSize: 11, color: "text.secondary", fontWeight: 600 }}
                >
                  Adjusted (fair)
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontSize: 11, color: "text.secondary", fontWeight: 600 }}
                >
                  Shift
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {zones.map((z) => {
                const rawN = rawByZone.get(z) ?? 0;
                const adjN = adjByZone.get(z) ?? 0;
                const shift = adjN - rawN;
                const colour = shift > 0 ? "#00583D" : shift < 0 ? "#A32D2D" : "text.disabled";
                return (
                  <TableRow key={z}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <ZoneDot zone={z} /> {z}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {fmtInt(rawN)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {fmtInt(adjN)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontVariantNumeric: "tabular-nums", color: colour, fontWeight: 600 }}
                    >
                      {shift === 0 ? "—" : `${shift > 0 ? "▲ +" : "▼ −"}${fmtInt(Math.abs(shift))}`}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Box>

      <WorkedExample
        title="Worked example — one member"
        intro="An illustration (made-up member, realistic pattern). Over the last 12 months Elena turned up to events across these zones:"
        columns={["Zone", "Events we offered", "She turned up to", "Share she took"]}
        rows={[
          ["YELLOW · 06–09 UTC", "2,200", "16", "0.7%"],
          ["GREEN · 09–12 UTC", "1,900", "15", "0.8%"],
          ["RED · 00–03 UTC ✓", "1,350", "13", "1.0%"],
        ]}
        conclusion={
          <>
            Elena came to the most events in <b>YELLOW and GREEN</b> — our busiest slots — so by raw
            count her zone <b>looks YELLOW</b>. But per chance offered she turned up most in{" "}
            <b>RED</b>: <b>1.0%</b> of RED events vs <b>0.7%</b> of YELLOW. So her{" "}
            <b>real zone is RED</b> (✓): the quieter slot she genuinely shows up for, once you
            account for how few RED events we run. (Real flips are often this subtle — we schedule
            fairly evenly, so the rate gaps are small but real.)
          </>
        }
      />
    </Box>
  );
};

export default MemberZonePanel;
